import React, {useCallback, useContext, useEffect, useState} from 'react';
import SectionHeader from '../../../util/component/SectionHeader';
import {Box, Button, Grid, Paper, useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {GlobalContext} from '../../../util/state/GlobalContextProvider';
import * as yup from 'yup';
import {FormProvider, useForm} from 'react-hook-form';
import ProfileData from '../component/ProfileData';
import CustomContentWrapper from '../../../util/component/CustomContentWrapper';
import {yupResolver} from "@hookform/resolvers/yup";
import axios from "axios";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {useMutation, useQuery} from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {profileDetailStructure} from "../state/profileState";
import {useHasScope} from "../../../config/globalConfig";
import NotAuthorizedDialog from "../../authentication/component/NotAuthorizedDialog";

interface ProfileCreateProps {
    name: string | null;
    legalEntity: any;
    department: any;
    auths: any[];
}

const ProfileCreate = () => {
    const hasScope = useHasScope;
    const theme = useTheme();
    const {t} = useTranslation(['operator']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities);
    const [departments] = useState<any[]>(globalData.user?.departments);
    const [profiles, setProfiles] = useState<any[]>([]);
    const setProfileDetailStructure = useSetRecoilState(profileDetailStructure);
    const {profileDtos} = useRecoilValue(profileDetailStructure);

    useEffect(() => {
        setProfiles(profileDtos);
    }, [profileDtos]);

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .nullable(true)
            .trim()
            .required('message.validation.fieldRequired'),
        legalEntity: yup
            .object()
            .required('message.validation.fieldRequired'),
        department: yup
            .object()
            .required('message.validation.fieldRequired'),
    });

    const methods = useForm<ProfileCreateProps>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: null,
            legalEntity: legalEntities.length === 1 ? legalEntities[0] : null,
            department: departments.length === 1 ? departments[0] : null,
            auths: [],
        },
    });

    const {handleSubmit, formState: {defaultValues}} = methods;

    const createOnSuccess = useCallback((response: any) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('translation:success.createOperation'),
                    type: snackbarTypes.success,
                    show: true,
                    autoClose: true,
                },
            },
        });
    }, [dispatchGlobalData, t])

    const createOnError = useCallback((error: any) => {
        console.log(error);
        if (error.response.status === 400) {
            let errorMsg = t('translation:error.badRequest');
            JSON.parse(error.response.data.errorDetails).forEach((e: string) => {
                errorMsg = errorMsg.concat('\n').concat(t('translation:error.'.concat(e)));
            });
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: errorMsg,
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('translation:error.createOperation'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
        }
    }, [dispatchGlobalData, t])

    const createProfile = useMutation({
        mutationFn: (params: any) => {
            params.auths = params.auths.filter((auth: any) => !!auth.id);
            return axios({
                method: 'post',
                url: '/profile',
                data: params,
            })
        },
        onSuccess: createOnSuccess,
        onError: createOnError
    })

    const findProfileDetailStructure = () => {
        return axios.get('/profile/structure')
    }

    const onSuccess = (response: any) => {
        setProfileDetailStructure(response.data);
    }
    const onError = (error: any) => {
        console.log(error);
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t('message.error.tryLater'),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    const {isFetching} = useQuery(['findProfileDetailStructure'], findProfileDetailStructure, {
        onSuccess,
        onError
    })

    return (

        <Box>
            <SectionHeader
                title={t('label.createProfile')}
            />
            <CustomContentWrapper>
                {hasScope('SCOPE_CREATE_PROFILE') ? <FormProvider {...methods}>
                    <form onSubmit={handleSubmit((params: any) => createProfile.mutate(params))}>
                        <Paper style={{
                            borderRadius: "4px",
                            padding: theme.spacing(1) + ' ' + theme.spacing(2),
                            marginBottom: theme.spacing(2)
                        }}
                               elevation={1}
                        >
                            <Grid container spacing={2}>
                                <ProfileData
                                    defaultValues={defaultValues}
                                    legalEntities={legalEntities}
                                    departments={departments}
                                    profiles={profiles}
                                />
                                {hasScope('SCOPE_CREATE_PROFILE') && <Grid item xs={12}>
                                    <Box mt={2}>
                                        <Button
                                            type="submit"
                                            fullWidth
                                        >
                                            {t('action.save')}
                                        </Button>
                                    </Box>
                                </Grid>}
                            </Grid>
                        </Paper>
                    </form>
                </FormProvider>
                : <NotAuthorizedDialog/>}
            </CustomContentWrapper>
            <CustomLoader loading={isFetching || createProfile.isLoading}/>
        </Box>

    );
};

export default ProfileCreate;

